import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2 } from "lucide-react";
import { US_STATES } from "@/lib/constants";
import { toast } from "sonner";

export default function RegisterClient() {
  const { registerClient } = useAuth();
  const navigate = useNavigate();
  const pendingPlan = new URLSearchParams(window.location.search).get("plan") || localStorage.getItem("sj_pending_plan");
  const [f, setF] = useState({ first_name: "", last_name: "", company_name: "", phone: "", email: "", password: "", business_type: "", num_properties: "", city: "", state: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target?.value ?? e });

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await registerClient(f);
      toast.success("Account created!");
      if (pendingPlan) { navigate("/app/client/plans?plan=" + pendingPlan); return; }
      navigate("/app/client");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-6">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center"><Building2 className="w-5 h-5 text-white" /></div>
          <span className="font-head font-bold text-lg text-slate-900">SJ Property Care</span>
        </Link>
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 md:p-8">
          <h1 className="font-head text-2xl md:text-3xl font-semibold text-slate-900">Create Property Manager Account</h1>
          <p className="mt-2 text-slate-500 text-sm">Start managing your maintenance in one place.</p>
          <form onSubmit={submit} className="mt-6 grid sm:grid-cols-2 gap-4">
            <div><Label>First Name</Label><Input data-testid="reg-first-name" required value={f.first_name} onChange={set("first_name")} className="mt-1.5" /></div>
            <div><Label>Last Name</Label><Input data-testid="reg-last-name" required value={f.last_name} onChange={set("last_name")} className="mt-1.5" /></div>
            <div className="sm:col-span-2"><Label>Company Name</Label><Input data-testid="reg-company" required value={f.company_name} onChange={set("company_name")} className="mt-1.5" /></div>
            <div><Label>Phone</Label><Input data-testid="reg-phone" required value={f.phone} onChange={set("phone")} className="mt-1.5" /></div>
            <div><Label>Email</Label><Input data-testid="reg-email" type="email" required value={f.email} onChange={set("email")} className="mt-1.5" /></div>
            <div className="sm:col-span-2"><Label>Password</Label><PasswordInput data-testid="reg-password" required value={f.password} onChange={set("password")} className="mt-1.5" /></div>
            <div>
              <Label>Business Type</Label>
              <Select value={f.business_type} onValueChange={set("business_type")}>
                <SelectTrigger data-testid="reg-business-type" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {["Residential Property Manager", "Commercial Property Manager", "Landlord", "Real Estate Investor", "HOA", "Short-Term Rental Operator", "Apartment Community"].map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Number of Properties</Label><Input data-testid="reg-num-props" value={f.num_properties} onChange={set("num_properties")} className="mt-1.5" placeholder="e.g. 12" /></div>
            <div><Label>City</Label><Input data-testid="reg-city" required value={f.city} onChange={set("city")} className="mt-1.5" /></div>
            <div>
              <Label>State</Label>
              <Select value={f.state} onValueChange={set("state")}>
                <SelectTrigger data-testid="reg-state" className="mt-1.5"><SelectValue placeholder="State" /></SelectTrigger>
                <SelectContent className="max-h-64">{US_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {error && <p className="sm:col-span-2 text-sm text-rose-600">{error}</p>}
            <div className="sm:col-span-2">
              <Button data-testid="reg-submit" type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 h-11">{loading ? "Creating..." : "Create Account"}</Button>
            </div>
          </form>
          <p className="mt-4 text-sm text-slate-500">Already registered? <Link to="/login" className="text-blue-600 font-medium">Sign in</Link></p>
        </div>
      </div>
    </div>
  );
}
